import { lazy, Suspense, useEffect, useState } from "react";
import { BasketPanel } from "./components/Basket.tsx";
import { CommandPalette, OPEN_SEARCH_EVENT } from "./components/CommandPalette.tsx";
import { Header } from "./components/Header.tsx";
import { btn } from "./components/ui.tsx";
import { useIsDesktop } from "./hooks/useMediaQuery.ts";
import { onLinkClick, useRoute } from "./hooks/useRouter.ts";
import { DiscoverPage } from "./pages/DiscoverPage.tsx";
import { StoryPage } from "./pages/StoryPage.tsx";
import { AppStateProvider, useApp } from "./state/AppState.tsx";

// Browse (home) and story pages ship in the main bundle; other pages load on first visit.
const MapPage = lazy(() => import("./pages/MapPage.tsx").then((m) => ({ default: m.MapPage })));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage.tsx").then((m) => ({ default: m.GlossaryPage })));
const RoundupPage = lazy(() => import("./pages/RoundupPage.tsx").then((m) => ({ default: m.RoundupPage })));
const TrendsPage = lazy(() => import("./pages/TrendsPage.tsx").then((m) => ({ default: m.TrendsPage })));
const DigestPage = lazy(() => import("./pages/DigestPage.tsx").then((m) => ({ default: m.DigestPage })));
const BriefingPage = lazy(() => import("./pages/BriefingPage.tsx").then((m) => ({ default: m.BriefingPage })));
const SharedListPage = lazy(() => import("./pages/SharedListPage.tsx").then((m) => ({ default: m.SharedListPage })));
const ReadingSession = lazy(() => import("./pages/ReadingSession.tsx").then((m) => ({ default: m.ReadingSession })));

export function App() {
  return (
    <AppStateProvider>
      <Shell />
    </AppStateProvider>
  );
}

function Shell() {
  const app = useApp();
  const route = useRoute();
  const isDesktop = useIsDesktop();
  const [searchOpen, setSearchOpen] = useState(false);

  // Ctrl+K / ⌘K anywhere; "/" when not typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = !!target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey) && !e.altKey) {
        e.preventDefault();
        setSearchOpen((o) => !o);
      } else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    const open = () => setSearchOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, open);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_SEARCH_EVENT, open);
    };
  }, []);

  return (
    <>
      <a href="#main" className="sr-only z-50 rounded-lg bg-ink px-4 py-2 text-white focus:not-sr-only focus:fixed focus:left-3 focus:top-3">
        Skip to content
      </a>
      <Header route={route} onSearch={() => setSearchOpen(true)} />
      {app.notice && (
        <div role="status" className="mx-auto mt-3 flex max-w-3xl items-start gap-3 rounded-xl border border-line bg-card px-4 py-2.5 text-sm shadow-sm mx-4 sm:mx-auto">
          <p className="flex-1">{app.notice}</p>
          <button type="button" className="font-semibold text-coral-deep" onClick={app.dismissNotice}>Dismiss</button>
        </div>
      )}
      <main id="main" className={`mx-auto max-w-[90rem] transition-[padding] ${isDesktop && app.basketOpen ? "pr-[23rem]" : ""}`}>
        <Suspense fallback={<p role="status" className="px-4 py-16 text-center text-sm text-ink-soft">Loading…</p>}>
        {app.configErrors.length > 0 || !app.repo ? (
          <ConfigError errors={app.configErrors} />
        ) : route === "digest" ? (
          <DigestPage />
        ) : route === "discover" ? (
          <DiscoverPage />
        ) : route === "map" ? (
          <MapPage />
        ) : route === "glossary" ? (
          <GlossaryPage />
        ) : route === "roundup" ? (
          <RoundupPage />
        ) : route === "trends" ? (
          <TrendsPage />
        ) : route === "briefing" ? (
          <BriefingPage />
        ) : route === "shared" ? (
          <SharedListPage />
        ) : route === "read" ? (
          <ReadingSession />
        ) : route === "story" ? (
          <StoryPage />
        ) : (
          <div className="px-4 py-20 text-center">
            <h1 className="font-display text-3xl font-semibold">Page not found</h1>
            <a href="/" onClick={onLinkClick} className={`${btn.primary} mt-5`}>Go to Browse</a>
          </div>
        )}
        </Suspense>
      </main>
      <footer className="mx-auto max-w-3xl px-4 pb-24 pt-6 text-center text-xs leading-relaxed text-ink-soft lg:pb-8">
        Databricks Pulse is a reader of public Databricks release notes, blog posts, and announcements. Card text is quoted from the linked official source unless a Pulse editor summarized it. “Why it matters” lines tagged AI-generated are written by AI from that text: they can be wrong and are not official Databricks statements. Always check the linked source. Pulse counts story opens, source clicks, and saves anonymously (no account, cookie, or IP; country is estimated from your device time zone) to show trends.
      </footer>
      <BasketPanel />
      {app.repo && <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />}
    </>
  );
}

function ConfigError({ errors }: { errors: string[] }) {
  return (
    <div role="alert" className="mx-auto mt-10 max-w-xl rounded-xl border border-coral/40 bg-coral-wash p-6">
      <h1 className="font-display text-xl font-semibold">Databricks Pulse isn't configured</h1>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
        {(errors.length ? errors : ["Firebase services are unavailable."]).map((e) => <li key={e}>{e}</li>)}
      </ul>
      <p className="mt-3 text-sm">No sample data is shown in this mode. See README → “Configure a Firebase project”.</p>
    </div>
  );
}
